import { useDispatch } from 'react-redux';
import React, { useCallback, useEffect, useRef } from 'react';
import { assoc, cond, find, forEach, head, mergeLeft, mergeRight, omit, path, pick, pipe, prop, propEq, reduce, T, toPairs, when } from 'ramda';
import { doDeposit, getTransactionPlayerMsg, methodNotification } from '../actions';
import { getURLParams, getLocationParams } from 'js/utils/url.util';
import { EXTERNAL_ERRORS, INITIATORS, INTERNAL_ERROR, POST_MESSAGE_ERRORS } from '../constants';
import { handleSpecialPmsDeposit } from './withDepositSpecial';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { useConfirmationPopup } from 'js/components/common/popups/ConfirmationPopup';
import { performRedirect } from '../utils/redirect';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { addBackUrls, addClientType, addNoPlayerMessageToGs, addStoreCardConfirmed, get3dsCallParams, getAccount } from './helpers';
import { usePortletConfig, usePortletSelector, usePortletTranslations } from 'js/components/system/Portlet';
import { useMethodSelector } from '../withCashierPortlet';
import { dispatcher } from 'dispatcher';
import { clearUsedPromoCodeFromStore } from 'js/utils/promocode.util';

export const confirmationFields = [
    'beneficialOwnerConfirmed',
    'feeConfirmed',
    'currencyConversion',
    'depositConfirmed',
    'loseBonus',
    'profitTaxConfirmed'
];

export const resolveStoredAccount = params => ({ storedAccountId, ...data }) => {
    let result = { ...data };
    const { accounts = [], isCreditCard, isGeneric } = params;

    if (storedAccountId == null) {
        return result;
    }

    if (!isGeneric || isCreditCard) {
        result.accountCode = storedAccountId;
    }

    if (!isCreditCard) {
        const storedAccount = getAccount({ accounts, accountCode: storedAccountId });

        if (!storedAccount) {
            throw new Error('FE Cashier Error: Selected account not found');
        }

        result = {
            ...storedAccount.accountData,
            ...result
        };
    }

    return result;
};

export const addSpecificParams = (params, newData) => (request) => {
    const specificParams = {
        ...omit([...confirmationFields, 'accountCode', 'amount', 'promoCode'], newData),
        ...get3dsCallParams(params)
    };

    return {
        ...request,
        specificParams
    };
};

export const addTransactionTag = ({ isQuickDeposit }) => (request) => {
    const transactionTags = { transactionTag: [{ name: 'cashiertype', value: 'HUB2' }] };

    if (isQuickDeposit) {
        transactionTags.transactionTag.push({ name: 'quickdeposit', value: 1 });
    }

    return {
        ...request,
        transactionTags
    };
};

export const getRequestData = ({ code, clientType, clientPlatform }) => ({ promoCode, amount, accountCode }) => ({
    methodCode: code,
    ...clientType && { clientType },
    ...clientPlatform && { clientPlatform },
    promoCode,
    amount,
    accountCode
});

export const handleVisaCheckout = ({ accounts }) => when(
    propEq('code', 'VisaCheckout'),
    ({ accountCode }) => assoc('methodCode', path(['pmCode'], getAccount({ accounts, accountCode })))
);

export const handleSubMethods = ({ isSubMethod, methodCode, code }) => when(
    () => isSubMethod,
    request => ({
        ...request,
        methodCode,
        subMethodsCodes: [code]
    })
);

export const prepareDepositRequest = params => (data) => {
    const newData = resolveStoredAccount(params)(data);

    return pipe(
        getRequestData(params),
        handleVisaCheckout(params),
        handleSubMethods(params),
        addBackUrls({ ...params, action: 'deposit' }),

        addSpecificParams(params, newData),
        handleSpecialPmsDeposit(params),

        addClientType(params),
        addNoPlayerMessageToGs(params),
        addStoreCardConfirmed(params),
        addTransactionTag(params),

        mergeLeft(pick(confirmationFields, newData))
    )(newData);
};

export const prepare3dsMethodNotificationRequest = ({ error, clientIp, ...params }, headerParameters) => pipe(
    get3dsCallParams,
    toPairs,
    reduce(
        (memo, [key, value]) => ({ ...memo, [key.replace(/^threeDSB/, 'b')]: value }),
        {}
    ),
    mergeRight({
        transactionId: params.transactionId || head(headerParameters['X-Transaction-Code']),
        threeDSMethodCompletionInd: error ? 'N' : 'Y'
    })
)({ clientIp });

export const handleKnownErrors = (knownErrors, error) => find(propEq('code', error.code), knownErrors);

export const handle3dSecure = async (redirectResponseParams, headerParameters, options) => {
    const { status, error, ...params } = redirectResponseParams;
    const needSendRequest = status === '3DsNotification' || error === 'LIGHTBOX_TIMED_OUT_10S';

    if (needSendRequest) {
        const { clientIp } = options;
        const result = await methodNotification(
            prepare3dsMethodNotificationRequest({ error, clientIp, ...params }, headerParameters)
        );
        await successDeposit(result, options);
    }
};

export const showTransactionNotification = (status, content, { t, showNotification }) =>
    showNotification(
        content ? <span dangerouslySetInnerHTML={{ __html: content }} /> : t(`transaction.${status}`),
        { variant: status === 'error' ? 'error' : 'success' }
    );

export const handleTransactionPlayerMsg = async (
    status,
    action,
    transactionCode,
    options
) => {
    try {
        const { data: { actions } } = await getTransactionPlayerMsg({
            type: action,
            transactionId: transactionCode
        });

        forEach(({ showMessageNotificationInfo: { message } }) =>
            showTransactionNotification(status, message, options), actions);
    } catch (err) {
        const { data: { errorMessage } } = err;
        showTransactionNotification(status, errorMessage, options);
    }
};

export const handleTransactionStatus = async (params, { clientPlatform, ...options }) => {
    const statuses = ['success', 'error', 'cancel', 'waiting'];
    const { status: originStatus, action, initiator, transactionCode = '' } = params;
    const status = originStatus === 'success' ? 'success' : 'error';

    if (initiator !== INITIATORS.CASHIER_QD) {
        return;
    }

    history.replaceState(history.state, window.title, location.origin + location.pathname);

    if (statuses.includes(originStatus)) {
        cond([
            [() => status === 'error' && clientPlatform === 'download', T],
            [() => status === 'error', () => showTransactionNotification(status, null, options)],
            [() => transactionCode.length, () => handleTransactionPlayerMsg(status, action, transactionCode, options)]
        ])();
    }

    if (originStatus === 'error') {
        throw new Error(INTERNAL_ERROR);
    }
};

export const handleRedirectMessage = async (redirectResponse, depositResponse, options) => {
    if (!redirectResponse) {
        return;
    }

    const { data: { headerParameters } } = depositResponse;
    const is3DSecure = Boolean(path(['X-Redirect-Type', '0'], headerParameters));
    const { error, href = window.location.href } = redirectResponse;
    const { search } = new window.URL(href, document.baseURI);
    const urlParams = getURLParams(search);
    const notifyData = error ? { status: 'error', initiator: INITIATORS.CASHIER_QD } : urlParams;

    if (is3DSecure && error !== POST_MESSAGE_ERRORS.LIGHTBOX_CLOSED) {
        return await handle3dSecure({ error, ...urlParams }, headerParameters, options);
    }

    return await handleTransactionStatus(notifyData, options);
};

export const successDeposit = async (response, options, extWindow) => {
    const { popupsDispatch, redirectType } = options;

    if (response.data.headerParameters) {
        // TODO:
        // - handle DataTrans before
        const result = await performRedirect(response, { redirectType, extWindow: extWindow.current }, popupsDispatch);
        await handleRedirectMessage(result, response, options);
    }
    // TODO:
    // - show buy-in popup
    // - update user payment statistics

    // for public event
    dispatcher.dispatch('deposit:success', response.data);
};

export const errorDeposit = options => (err) => {
    const { data, depositRef, showConfirmationPopup, t } = options;
    const { response } = err;

    if (!response) {
        return t('playtech.mobile.system.error.no.response');
    }

    if (response.message === INTERNAL_ERROR) {
        return;
    }

    const {
        data: {
            error: {
                description = 'playtech.mobile.system.error.default',
                message = description,
                severity = 'Warning'
            } = {},
            error
        }
    } = response;

    const knownError = handleKnownErrors(EXTERNAL_ERRORS, error);

    if (knownError) {
        const newData = {
            ...data,
            [knownError.type]: true
        };

        return new Promise(resolve =>
            showConfirmationPopup({
                onAccept: () => resolve(depositRef.current(newData)),
                onClose: resolve,
                title: severity,
                content: message
            }));
    }

    // for public event
    dispatcher.dispatch('deposit:fail', response.data);

    return message;
};

export default Wrapped => (props) => {
    const { afterSuccessDeposit } = props;
    const method = useMethodSelector();
    const { initiator } = usePortletConfig();
    const isQuickDeposit = usePortletSelector(prop('isQuickDeposit'));
    const clientPlatform = useDeviceSelector(prop('clientPlatform'));
    const { themeName, cashierClientType, sendCashierMessagesToGS } = useAppSelector(pick([
        'themeName',
        'cashierClientType',
        'sendCashierMessagesToGS'
    ]));
    const { clientIp, storeCardConfirmationRequired } = useUserSelector(pick([
        'clientIp',
        'storeCardConfirmationRequired'
    ]));
    const showNotification = useShowNotification();
    const showConfirmationPopup = useConfirmationPopup();
    const dispatch = useDispatch();
    const t = usePortletTranslations();
    const extWindow = useRef();
    const depositRef = useRef();
    const { clientType, clientPlatform: clientPlatformUrl } = getLocationParams();

    const deposit = useCallback(async (data) => {
        const { promoCode } = data;
        const { redirectType } = method;

        if (redirectType === 'popup') {
            extWindow.current = window.open('', '', 'resizable=yes, width=800px, height=600px');
        }

        const depositParams = {
            ...method,
            afterSuccessDeposit,
            initiator,
            isQuickDeposit,
            themeName,
            cashierClientType,
            sendCashierMessagesToGS,
            clientIp,
            storeCardConfirmationRequired,
            ...clientType && { clientType },
            ...clientPlatformUrl && { clientPlatform: clientPlatformUrl }
        };

        const successDepositOptions = {
            popupsDispatch: dispatch,
            redirectType,
            clientPlatform,
            showNotification,
            clientIp,
            t
        };

        try {
            const depositResult = await doDeposit(prepareDepositRequest(depositParams)(data));
            await successDeposit(depositResult, successDepositOptions, extWindow);
            afterSuccessDeposit?.();
            clearUsedPromoCodeFromStore(promoCode);
        } catch (err) {
            extWindow.current?.close();
            return errorDeposit({ data, depositRef, showConfirmationPopup, t })(err);
        }
    }, [
        clientType,
        clientPlatformUrl,
        method,
        afterSuccessDeposit,
        initiator,
        isQuickDeposit,
        themeName,
        cashierClientType,
        sendCashierMessagesToGS,
        clientIp,
        storeCardConfirmationRequired,
        dispatch,
        clientPlatform,
        showNotification,
        t,
        showConfirmationPopup
    ]);

    depositRef.current = deposit;

    // withTransactionStatusOnInit
    useEffect(() => {
        const params = getURLParams(window.location.search);

        if ('status' in params) {
            handleTransactionStatus(params, { t, clientPlatform, showNotification }).then(afterSuccessDeposit);
        }
    }, [afterSuccessDeposit, clientPlatform, showNotification, t]);

    return <Wrapped {...props} deposit={deposit} />;
};
